<template>
  <main class="app-content">
    <div class="app-title">
      <h1><font-awesome-icon icon="robot"/> AI Trợ lý - Tổng quan</h1>
    </div>

    <div class="row mb-3">
      <div class="col-auto">
        <b-form-group label="Từ ngày">
          <b-form-input type="date" v-model="filterFrom" style="width: 160px; cursor: pointer;" @click.native="$event.target.showPicker && $event.target.showPicker()"/>
        </b-form-group>
      </div>
      <div class="col-auto">
        <b-form-group label="Đến ngày">
          <b-form-input type="date" v-model="filterTo" style="width: 160px; cursor: pointer;" @click.native="$event.target.showPicker && $event.target.showPicker()"/>
        </b-form-group>
      </div>
      <div class="col-auto d-flex align-items-end">
        <b-button variant="primary" @click="load" class="mb-3">
          <font-awesome-icon icon="search"/> Tìm kiếm
        </b-button>
      </div>
    </div>

    <!-- Alert banner -->
    <b-alert v-if="alert && alert.isAlert" variant="danger" show dismissible class="mb-4">
      <h5 class="mb-1"><font-awesome-icon icon="exclamation-triangle"/> Cảnh báo: Tỉ lệ lỗi cao!</h5>
      <p class="mb-1">Trong 1 giờ qua: <strong>{{ alert.error }}/{{ alert.total }}</strong> câu hỏi bị lỗi ({{ alert.errorRate }}%)</p>
      <div v-if="alert.recentErrors && alert.recentErrors.length" class="mt-2">
        <small><strong>Lỗi gần đây:</strong></small>
        <ul class="mb-1 pl-3">
          <li v-for="(err, i) in alert.recentErrors.slice(0, 5)" :key="i">
            <small>
              {{ err.createTime | moment('HH:mm') }} -
              <span v-if="err.client">{{ err.client.companyName || err.client.contactName || err.client.domain }}</span>
              <span v-if="err.user"> / {{ err.user.name }}</span>:
              {{ err.question ? err.question.substring(0, 60) : '' }}
              <span class="text-danger" v-if="err.errorMessage"> — {{ err.errorMessage.substring(0, 80) }}</span>
            </small>
          </li>
        </ul>
      </div>
      <b-button size="sm" variant="outline-danger" @click="sendAlertSlack" :disabled="sendingAlert" class="mt-1">
        <font-awesome-icon icon="bell"/> Gửi cảnh báo Slack
      </b-button>
    </b-alert>

    <!-- Summary cards — dòng 1: Tổng + Thời gian TB -->
    <div class="row mb-3 justify-content-center">
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-muted mb-1">Tổng câu hỏi</h5>
          <h2 class="mb-0">{{ stats.total || 0 }}</h2>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-muted mb-1">Thời gian TB</h5>
          <h2 class="mb-0">{{ stats.avgDuration ? (stats.avgDuration / 1000).toFixed(1) + 's' : '—' }}</h2>
        </div>
      </div>
    </div>
    <!-- Summary cards — dòng 2: 3 trạng thái -->
    <div class="row mb-4 justify-content-center">
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-success mb-1">Thành công</h5>
          <h2 class="mb-0 text-success">{{ stats.success || 0 }}</h2>
          <small class="text-muted" v-if="stats.total">{{ ((stats.success / stats.total) * 100).toFixed(1) }}%</small>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-danger mb-1">Lỗi</h5>
          <h2 class="mb-0 text-danger">{{ stats.error || 0 }}</h2>
          <small class="text-muted" v-if="stats.total">{{ ((stats.error / stats.total) * 100).toFixed(1) }}%</small>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-warning mb-1">Không dùng tool</h5>
          <h2 class="mb-0 text-warning">{{ stats.noTool || 0 }}</h2>
          <small class="text-muted" v-if="stats.total">{{ ((stats.noTool / stats.total) * 100).toFixed(1) }}%</small>
        </div>
      </div>
    </div>

    <!-- Charts row -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="card p-3">
          <h5>Xu hướng sử dụng</h5>
          <apexchart v-if="stats.trend && stats.trend.length" type="bar" height="300" :options="trendOptions" :series="trendSeries" :key="'trend-' + trendKey"/>
          <p v-else class="text-muted text-center py-5">Chưa có dữ liệu trong khoảng thời gian đã chọn</p>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card p-3">
          <h5>Top Tools</h5>
          <div v-if="stats.topTools && stats.topTools.length">
            <div v-for="tool in stats.topTools" :key="tool._id" class="d-flex justify-content-between align-items-center py-1 border-bottom">
              <span>{{ toolLabels[tool._id] || tool._id }}</span>
              <b-badge variant="primary">{{ tool.count }}</b-badge>
            </div>
          </div>
          <p v-else class="text-muted text-center py-5">Chưa có dữ liệu</p>
        </div>
      </div>
    </div>

    <!-- Top clients -->
    <div class="row">
      <div class="col-md-12">
        <div class="card p-3">
          <h5>Top khách hàng sử dụng</h5>
          <b-table v-if="stats.topClients && stats.topClients.length" :items="stats.topClients" :fields="clientFields" small striped>
            <template #cell(name)="data">
              {{ data.item.name || '—' }}
            </template>
            <template #cell(domain)="data">
              {{ data.item.domain || '—' }}
            </template>
          </b-table>
          <p v-else class="text-muted text-center py-3">Chưa có dữ liệu</p>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    var now = new Date();
    var thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      filterFrom: thirtyDaysAgo.toISOString().split('T')[0],
      filterTo: now.toISOString().split('T')[0],
      stats: {},
      alert: null,
      sendingAlert: false,
      clientFields: [
        {key: 'name', label: 'Khách hàng'},
        {key: 'domain', label: 'Domain'},
        {key: 'code', label: 'Mã'},
        {key: 'count', label: 'Số câu hỏi', class: 'text-right'},
      ],
      toolLabels: {
        search: 'Tìm kiếm',
        report: 'Báo cáo',
        list_my_items: 'Danh sách của tôi',
        info_customer: 'Xem khách hàng',
        create_customer: 'Tạo khách hàng',
        update_customer: 'Cập nhật khách hàng',
        click_to_call: 'Gọi điện',
        info_ticket: 'Xem ticket',
        create_ticket: 'Tạo ticket',
        update_ticket: 'Cập nhật ticket',
        list_my_tickets: 'DS ticket của tôi',
        info_task: 'Xem công việc',
        create_task: 'Tạo công việc',
        update_task: 'Cập nhật công việc',
        list_my_tasks: 'DS công việc của tôi',
        info_opportunity: 'Xem cơ hội',
        create_opportunity: 'Tạo cơ hội',
        update_opportunity: 'Cập nhật cơ hội',
        info_order: 'Xem đơn hàng',
        info_order_table: 'Xem lịch hẹn',
        check_availability: 'Kiểm tra lịch trống',
        create_order_table: 'Tạo lịch hẹn',
        update_order_table: 'Cập nhật lịch hẹn',
        info_product: 'Xem sản phẩm',
        info_contract: 'Xem hợp đồng',
        info_expense: 'Xem phiếu thu/chi',
        info_debt: 'Xem công nợ',
        info_invoice: 'Xem hoá đơn',
        info_invoice_product: 'Xem HĐ sản phẩm',
        info_invoice_buyer: 'Xem HĐ người mua',
        info_shipping: 'Xem vận chuyển',
        info_company: 'Xem công ty',
        account_info: 'Thông tin tài khoản',
        call_history: 'Lịch sử cuộc gọi',
        team_info: 'Thông tin nhóm',
        navigation_guide: 'Hướng dẫn sử dụng',
        navigation_overview: 'Tổng quan hệ thống',
        knowledge_base: 'Kiến thức',
        list_automation: 'Xem automation',
        create_automation: 'Tạo automation',
        toggle_automation: 'Bật/tắt automation',
        automation_log: 'Log automation',
        list_report_automation: 'Xem báo cáo tự động',
        create_report_automation: 'Tạo báo cáo tự động',
        toggle_report_automation: 'Bật/tắt báo cáo tự động',
        report_customer: 'Báo cáo khách hàng',
        report_call: 'Báo cáo cuộc gọi',
        report_ticket: 'Báo cáo ticket',
        report_task: 'Báo cáo công việc',
        report_opportunity: 'Báo cáo cơ hội',
        report_order: 'Báo cáo đơn hàng',
        report_overview: 'Báo cáo tổng quan',
        report_compare: 'Báo cáo so sánh',
        report_traffic: 'Báo cáo lưu lượng',
        report_expense: 'Báo cáo thu/chi',
        report_debt: 'Báo cáo công nợ',
        report_contract: 'Báo cáo hợp đồng',
        report_pipeline: 'Báo cáo pipeline',
        report_kpi: 'Báo cáo KPI',
        report_zns: 'Báo cáo ZNS',
        report_email_campaign: 'Báo cáo email campaign',
        report_call_campaign: 'Báo cáo call campaign',
        report_audio_quality: 'Báo cáo chất lượng âm',
        survey_info: 'Xem khảo sát',
        survey_summarize: 'Tổng hợp khảo sát',
        booking_info: 'Xem booking',
        booking_availability: 'Kiểm tra booking',
        booking_stats: 'Thống kê booking',
        create_product_order: 'Tạo đơn hàng',
        update_product_order: 'Cập nhật đơn hàng',
        list_booking_options: 'DS dịch vụ/nhân viên',
      },
    };
  },
  computed: {
    trendKey() {
      var trend = (this.stats.trend || []);
      return trend.length + '-' + (trend[0] ? trend[0]._id : '');
    },
    trendOptions() {
      var trend = (this.stats.trend || []);
      return {
        chart: {type: 'bar', stacked: true, toolbar: {show: false}},
        xaxis: {
          categories: trend.map(function(t) { return t._id; }),
          labels: {
            formatter: function(val) {
              if (!val) return val;
              var parts = val.split('-');
              return parts.length === 3 ? parts[2] + '/' + parts[1] : val;
            }
          }
        },
        colors: ['#28a745', '#dc3545', '#ffc107'],
        plotOptions: {bar: {columnWidth: '60%'}},
        dataLabels: {enabled: false},
      };
    },
    trendSeries() {
      var trend = (this.stats.trend || []);
      return [
        {name: 'Thành công', data: trend.map(function(t) { return t.success || 0; })},
        {name: 'Lỗi', data: trend.map(function(t) { return t.error || 0; })},
        {name: 'Không tool', data: trend.map(function(t) { return t.noTool || 0; })},
      ];
    },
  },
  methods: {
    load() {
      var from = new Date(this.filterFrom).getTime();
      var to = new Date(this.filterTo).getTime() + 24 * 60 * 60 * 1000 - 1;
      this.$service.get('/ai-assistant/stats', {params: {from: from, to: to}})
        .then(res => {
          this.stats = res.data;
        });
      this.checkAlert();
    },
    checkAlert() {
      this.$service.get('/ai-assistant/alert')
        .then(res => {
          this.alert = res.data;
        });
    },
    sendAlertSlack() {
      this.sendingAlert = true;
      this.$service.post('/ai-assistant/alert/notify')
        .then(function () {
          alert('Đã gửi cảnh báo đến Slack!');
        })
        .catch(function () {
          alert('Gửi cảnh báo thất bại');
        })
        .finally(function () {
          this.sendingAlert = false;
        }.bind(this));
    },
  },
  mounted() {
    this.load();
  },
};
</script>
